<template>
    <div data-test-view="reviews-analytics-snapshot">
        <!-- No Data -->
        <l-reviews-analytics-no-data v-if="isReportEmpty" />
        <!--/No Data -->

        <div v-if="hasReportData">
            <div style="margin-top: 10px">
                <l-chart
                    ref="chart"
                    type="radar"
                    height="200"
                    :colors="colors"
                    :data="data"
                    :options="options"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LChart from "@/components/LChart";
import LReviewsAnalyticsNoData from "@/components/LReviewsAnalyticsNoData";

export default {
    components: {
        LChart,
        LReviewsAnalyticsNoData,
    },
    props: {
        reportLoading: {
            type: Boolean,
            required: true,
        },
        isComparisonEnabled: {
            type: Boolean,
            required: true,
        },
    },
    computed: {
        ...mapGetters({
            report: "reviews/analytics/report",
        }),

        isReportEmpty() {
            return !this.reportLoading && this.report.noResults;
        },
        hasReportData() {
            return this.report.results?.list?.length;
        },
        colors() {
            return this.isComparisonEnabled
                ? [
                      "green",
                      "grey",
                      "orange",
                      "primary",
                      "orange",
                      "greenLow",
                      "greyLow",
                      "orangeLow",
                      "primaryLow",
                      "orangeLow",
                  ]
                : ["green", "grey", "orange", "primary", "orange"];
        },
        data() {
            const series = [
                this.$t("reviews.admin.analyticsSelfEvaluations"),
                this.$t("reviews.admin.analyticsPeerReviews"),
                this.$t("reviews.admin.analyticsDirectReportReviews"),
                this.$t("reviews.admin.analyticsManagerReviews"),
                this.$t("reviews.admin.analyticsAllReviews"),
            ];

            if (this.isComparisonEnabled) {
                series.push(
                    this.$t("analytics.labelComparison") +
                        ": " +
                        this.$t("reviews.admin.analyticsSelfEvaluations"),
                );
                series.push(
                    this.$t("analytics.labelComparison") +
                        ": " +
                        this.$t("reviews.admin.analyticsPeerReviews"),
                );
                series.push(
                    this.$t("analytics.labelComparison") +
                        ": " +
                        this.$t("reviews.admin.analyticsDirectReportReviews"),
                );
                series.push(
                    this.$t("analytics.labelComparison") +
                        ": " +
                        this.$t("reviews.admin.analyticsManagerReviews"),
                );
                series.push(
                    this.$t("analytics.labelComparison") +
                        ": " +
                        this.$t("reviews.admin.analyticsAllReviews"),
                );
            }

            const datasetOverride = this.isComparisonEnabled
                ? [
                      {
                          hidden: true,
                      },
                      {
                          hidden: true,
                      },
                      {
                          hidden: true,
                      },
                      {
                          hidden: true,
                      },
                      {
                          hidden: false,
                      },
                      {
                          hidden: true,
                      },
                      {
                          hidden: true,
                      },
                      {
                          hidden: true,
                      },
                      {
                          hidden: true,
                      },
                      {
                          hidden: false,
                      },
                  ]
                : [
                      {
                          hidden: false,
                      },
                      {
                          hidden: false,
                      },
                      {
                          hidden: false,
                          fill: false,
                      },
                      {
                          hidden: false,
                      },
                      {
                          hidden: true,
                          fill: false,
                      },
                  ];

            return {
                labels: Array.from(this.report.results?.graph?.labels ?? []),
                datasets: Array.from(this.report.results?.graph?.results ?? []).map(
                    (data, index) => ({
                        label: series[index],
                        data: Array.from(data),
                        ...datasetOverride[index % datasetOverride.length],
                    }),
                ),
            };
        },

        options() {
            const scaleType = this.report.settings?.scale?.type;

            if (this.isComparisonEnabled) {
                let options = {
                    layout: {
                        padding: {
                            left: 15,
                            right: 15,
                            top: 15,
                            bottom: 15,
                        },
                    },
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: {
                            boxWidth: 15,
                            padding: 20,
                        },
                    },
                    animation: false,
                    scale: {
                        pointLabels: {
                            callback: function(pointLabel) {
                                if (pointLabel.length > 25)
                                    return pointLabel.substring(0, 24) + "...";
                                else return pointLabel;
                            },
                        },
                        ticks: {
                            display: scaleType !== "100" ? true : false,
                            min: 1,
                            suggestedMax: scaleType ? scaleType : 5,
                            stepSize: scaleType !== "100" ? 1 : 20,
                        },
                    },
                    tooltips: {
                        enabled: true,
                        mode: "label",
                        callbacks: {
                            title: function(tooltipItems, data) {
                                var idx = tooltipItems[0].index;
                                return data.labels[idx];
                            },
                        },
                    },
                };

                if (scaleType === "100") {
                    // remove number references
                    options.scales = {
                        xAxes: [
                            {
                                display: false,
                            },
                        ],
                    };

                    options.tooltips.callbacks = {
                        label: function(tooltipItems, data) {
                            return data.datasets[tooltipItems.datasetIndex].label;
                        },
                    };
                }

                return options;
            }

            let options = {
                layout: {
                    padding: {
                        left: 20,
                        right: 20,
                        top: 20,
                        bottom: 20,
                    },
                },
                legend: {
                    display: true,
                    position: "bottom",
                    labels: {
                        boxWidth: 15,
                        padding: 20,
                    },
                },
                scale: {
                    pointLabels: {
                        callback: function(pointLabel) {
                            if (pointLabel.length > 25) return pointLabel.substring(0, 24) + "...";
                            else return pointLabel;
                        },
                    },
                    ticks: {
                        display: scaleType !== "100" ? true : false,
                        min: 0,
                        max: scaleType ? parseInt(scaleType) : 5,
                        beginAtZero: true,
                        stepSize: scaleType !== "100" ? 1 : 100,
                    },
                },
                tooltips: {
                    enabled: true,
                    mode: "label",
                    callbacks: {
                        title: function(tooltipItems, data) {
                            var idx = tooltipItems[0].index;
                            return data.labels[idx];
                        },
                    },
                },
            };

            // fix scale of bar chart

            options.scales = {
                xAxes: [
                    {
                        display: false,
                        ticks: {
                            display: true,
                            suggestedMax: scaleType ? scaleType : 5,
                            beginAtZero: true,
                        },
                    },
                ],
            };

            // remove numbers for continuous scale

            if (scaleType === "100") {
                options.tooltips.callbacks.label = function(tooltipItems, data) {
                    return data.datasets[tooltipItems.datasetIndex].label;
                };
            }

            return options;
        },
    },
};
</script>
